<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// No steps is no block for a visitor, heading included, and a step
// with nothing in it is left out; the editor keeps the empty block and
// its prompt (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const steps = computed(() => (Array.isArray(props.config.steps) ? props.config.steps : [])
    .filter((s) => editing || s?.title || s?.desc));

function pad2(n) { return n < 10 ? `0${n}` : `${n}`; }
</script>

<template>
    <section v-if="steps.length || editing" :id="config.anchor_id || null" class="th-block th-band th-band--short th-steps" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.eyebrow || config.heading || config.body" class="th-steps__head">
                <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                <h2 v-if="config.heading" class="th-h2">{{ config.heading }}</h2>
                <p v-if="config.body" class="th-lede th-steps__body">{{ config.body }}</p>
            </div>

            <ol v-if="steps.length" class="th-steps__list">
                <li v-for="(s, i) in steps" :key="i" class="th-steps__item">
                    <span class="th-steps__num">{{ pad2(i + 1) }}</span>
                    <h3 v-if="s.title" class="th-h3 th-steps__title">{{ s.title }}</h3>
                    <p v-if="s.desc" class="th-small">{{ s.desc }}</p>
                </li>
            </ol>
            <p v-else-if="editing" class="th-small th-steps__empty">Add steps in the editor.</p>
        </div>
    </section>
</template>

<style scoped>
.th-steps__head { max-width: 56ch; margin-bottom: clamp(32px, 5vw, 56px); }
.th-steps__body { margin-top: 14px; }

.th-steps__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
    gap: clamp(24px, 3vw, 32px);
}
/* The rule across the top of the row is the design's own border-top
   over a stat row: it numbers the steps in order without drawing a
   connector that breaks the moment the row wraps. */
.th-steps__item {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 18px;
    border-top: 2px solid var(--th-ink);
}
.th-steps__num {
    font: 500 12px/1 var(--th-mono);
    letter-spacing: .12em;
    color: var(--accent);
}
.th-steps__title { font-size: 20px; }
.th-steps__empty { font-style: italic; }
</style>
